(() => {
  if (window.NinhoBebeScratchDiscountLoaded) return;
  window.NinhoBebeScratchDiscountLoaded = true;

  const DAY = 86400000;
  const FADE_DURATION = 520;
  const EMAIL_REVEAL_DELAY = 720;
  const CONFETTI_LIFETIME = 1700;

  class LtScratchDiscountPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[data-scratch-dialog]');
      this.canvas = this.querySelector('[data-scratch-canvas]');
      this.status = this.querySelector('[data-scratch-status]');
      this.emailPanel = this.querySelector('[data-scratch-email-panel]');
      this.emailInput = this.querySelector('[data-scratch-email-input]');
      this.emailForm = this.querySelector('.lt-scratch-popup__email-form');
      this.emailFeedback = this.querySelector('[data-scratch-email-feedback]');
      this.serverEmailSuccess = Boolean(this.emailForm?.querySelector('[data-scratch-email-success]'));
      this.serverEmailError = Boolean(this.emailFeedback && !this.emailFeedback.hidden);
      this.redemptionPanel = this.querySelector('[data-scratch-redemption-panel]');
      this.useCouponButton = this.querySelector('[data-scratch-use-coupon]');
      this.useCouponLabel = this.useCouponButton?.textContent.trim() || '';
      this.revealButtons = this.querySelectorAll('[data-scratch-reveal]');
      this.closeButtons = this.querySelectorAll('[data-scratch-close]');
      this.couponCode = (this.dataset.couponCode || '').trim();
      this.redirectUrl = this.dataset.redirectUrl || this.useCouponButton?.getAttribute('href') || '/';
      this.threshold = Math.min(95, Math.max(35, Number(this.dataset.threshold) || 75)) / 100;
      this.delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.dismissDays = Math.max(1, Number(this.dataset.dismissDays) || 7);
      this.winDays = Math.max(1, Number(this.dataset.winDays) || 180);
      this.frequency = this.dataset.frequency || 'session';
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.storageKey = this.buildStorageKey();
      this.urlParams = new URLSearchParams(window.location.search);
      this.completed = false;
      this.pointerDown = false;
      this.lastPoint = null;
      this.raf = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.emailTimer = null;
      this.confettiTimer = null;
      this.confetti = null;
      this.previousActiveElement = null;

      this.bindEvents();
      this.schedule();
    }

    buildStorageKey() {
      const sectionId = this.dataset.sectionId || 'global';
      const campaignId = this.dataset.campaignId || 'default';
      const code = this.couponCode || 'coupon';
      return `lt_scratch_popup_${sectionId}_${campaignId}_${code}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    }

    bindEvents() {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close(this.dismissDays));
      });

      this.revealButtons.forEach((button) => {
        button.addEventListener('click', () => this.complete(true));
      });

      this.emailInput?.addEventListener('invalid', (event) => {
        event.preventDefault();
        this.showEmailError();
      });

      this.emailInput?.addEventListener('input', () => {
        if (this.emailInput.validity.valid) this.clearEmailFeedback();
      });

      this.useCouponButton?.addEventListener('click', (event) => this.handleUseCouponClick(event));

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close(this.dismissDays);
        if (event.key === 'Tab') this.trapFocus(event);
      });

      window.addEventListener('resize', () => {
        if (!this.hidden && this.canvas && !this.completed) this.drawCover();
      });
    }

    schedule() {
      if (!this.couponCode) return;
      if (this.urlParams.has('scratch_reset')) this.clearMemory();
      if (this.serverEmailSuccess || this.serverEmailError) {
        this.openTimer = window.setTimeout(
          () => this.open({ emailResult: this.serverEmailSuccess ? 'success' : 'error' }),
          0
        );
        return;
      }
      if (this.urlParams.has('scratch_email_submitted')) {
        // Shopify returns no success or error state when a valid email already has a customer record.
        this.openTimer = window.setTimeout(() => this.open({ emailResult: 'success' }), 0);
        return;
      }
      if (!this.shouldShow()) return;

      this.openTimer = window.setTimeout(() => this.open(), this.delay);
    }

    shouldShow() {
      if (this.urlParams.has('scratch_test') || this.isDesignMode || this.frequency === 'always') return true;
      if (this.frequency === 'session') {
        try {
          return !window.sessionStorage?.getItem(this.storageKey);
        } catch (error) {
          return true;
        }
      }
      return !this.hasActiveMemory();
    }

    hasActiveMemory() {
      const stored = this.readMemory();
      if (!stored) return false;
      if (!stored.expires || stored.expires < Date.now()) {
        this.clearMemory();
        return false;
      }
      return true;
    }

    readMemory() {
      let localValue = null;
      try {
        localValue = window.localStorage?.getItem(this.storageKey);
      } catch (error) {
        localValue = null;
      }

      if (localValue) return JSON.parse(localValue);

      const cookieValue = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith(`${this.storageKey}=`))
        ?.split('=')[1];
      return cookieValue ? { expires: Number(cookieValue) } : null;
    }

    remember(days, value = 'dismissed') {
      if (this.isDesignMode || this.frequency === 'always') return;
      if (this.frequency === 'session') {
        try {
          window.sessionStorage?.setItem(this.storageKey, value);
        } catch (error) {}
        return;
      }

      const expires = Date.now() + days * DAY;
      try {
        window.localStorage?.setItem(this.storageKey, JSON.stringify({ value, expires }));
      } catch (error) {}

      const cookieExpires = new Date(expires).toUTCString();
      document.cookie = `${this.storageKey}=${expires}; expires=${cookieExpires}; path=/; SameSite=Lax`;
    }

    clearMemory() {
      try {
        window.sessionStorage?.removeItem(this.storageKey);
      } catch (error) {}
      try {
        window.localStorage?.removeItem(this.storageKey);
      } catch (error) {}
      document.cookie = `${this.storageKey}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    }

    open({ emailResult = null } = {}) {
      if (!this.hidden) return;
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.emailTimer);
      this.previousActiveElement = document.activeElement;
      this.hidden = false;
      this.resetRevealState();
      if (this.frequency === 'session') this.remember(this.dismissDays, 'shown');
      this.classList.remove('is-closing');
      document.body.classList.add('lt-scratch-popup-open');
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      window.requestAnimationFrame(() => {
        this.drawCover();
        this.dialog?.focus();
        this.classList.add('is-visible');

        if (emailResult) {
          this.completed = true;
          this.classList.add('is-revealed');
          this.canvas?.classList.add('is-complete');
          this.revealButtons.forEach((button) => {
            button.hidden = true;
          });

          if (emailResult === 'success') {
            this.remember(this.winDays, 'won');
            this.showRedemptionPanel();
          } else {
            this.clearMemory();
            this.showEmailPanel();
            if (this.status) this.status.textContent = 'Preencha um e-mail válido.';
          }
        }
      });
    }

    close(days) {
      if (this.hidden) return;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.emailTimer);
      this.removeConfetti();
      this.classList.remove('is-visible');
      this.classList.add('is-closing');
      this.remember(days, this.completed ? 'won' : 'dismissed');
      this.closeTimer = window.setTimeout(() => {
        this.hidden = true;
        this.classList.remove('is-closing');
        document.body.classList.remove('lt-scratch-popup-open');
        document.body.style.overflow = this.previousBodyOverflow || '';
        this.previousActiveElement?.focus?.();
      }, FADE_DURATION);
    }

    resetRevealState() {
      this.completed = false;
      this.pointerDown = false;
      this.lastPoint = null;
      this.classList.remove('is-revealed', 'is-email-open', 'is-email-visible', 'is-redemption-visible');
      this.canvas?.classList.remove('is-complete', 'is-scratching');
      if (this.emailPanel) this.emailPanel.hidden = true;
      if (this.redemptionPanel) this.redemptionPanel.hidden = true;
      this.setUseCouponLoading(false);
      this.removeConfetti();
    }

    drawCover() {
      if (!this.canvas) return;
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 3);

      this.canvas.width = Math.round(width * pixelRatio);
      this.canvas.height = Math.round(height * pixelRatio);

      const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        this.complete(true);
        return;
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingEnabled = true;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#f8fcfc';
      ctx.fillRect(0, 0, width, height);

      const blueGlow = ctx.createRadialGradient(
        width * 0.56,
        height * 0.48,
        0,
        width * 0.56,
        height * 0.48,
        width * 0.66
      );
      blueGlow.addColorStop(0, 'rgba(255, 255, 255, 0.92)');
      blueGlow.addColorStop(0.52, 'rgba(243, 249, 249, 0.86)');
      blueGlow.addColorStop(1, 'rgba(208, 239, 241, 0)');
      ctx.fillStyle = blueGlow;
      ctx.fillRect(0, 0, width, height);

      const pinkGlow = ctx.createRadialGradient(
        width * 0.14,
        height * 0.16,
        0,
        width * 0.14,
        height * 0.16,
        width * 0.74
      );
      pinkGlow.addColorStop(0, 'rgba(255, 219, 232, 0.48)');
      pinkGlow.addColorStop(1, 'rgba(255, 219, 232, 0)');
      ctx.fillStyle = pinkGlow;
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = 'rgba(0, 176, 190, 0.12)';
      this.drawBurst(ctx, width * 0.57, height * 0.52, width * 0.15);
      this.drawBurst(ctx, width * 0.45, height * 0.58, width * 0.18);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.58)';
      for (let y = 18; y < height; y += 30) {
        for (let x = 18; x < width; x += 30) {
          ctx.beginPath();
          ctx.arc(x, y, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      const centerX = width / 2;
      const centerY = height / 2;
      const coverTitle = this.dataset.coverTitle || 'RASPE AQUI';
      let titleSize = Math.min(38, Math.max(22, width * 0.074));
      ctx.fillStyle = '#006375';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `900 ${titleSize}px Arial, sans-serif`;
      while (ctx.measureText(coverTitle).width > width * 0.84 && titleSize > 19) {
        titleSize -= 2;
        ctx.font = `900 ${titleSize}px Arial, sans-serif`;
      }
      ctx.fillText(coverTitle, centerX, centerY - height * 0.04);

      const coverSubtitle = this.dataset.coverSubtitle || '';
      if (coverSubtitle) {
        let subtitleSize = Math.min(16, Math.max(11, width * 0.032));
        ctx.fillStyle = '#454f5c';
        ctx.font = `800 ${subtitleSize}px Arial, sans-serif`;
        while (ctx.measureText(coverSubtitle).width > width * 0.82 && subtitleSize > 10) {
          subtitleSize -= 1;
          ctx.font = `800 ${subtitleSize}px Arial, sans-serif`;
        }
        ctx.fillText(coverSubtitle, centerX, centerY + height * 0.16);
      }

      this.canvas.classList.remove('is-complete');
      this.installScratchHandlers(ctx);
    }

    drawBurst(ctx, x, y, radius) {
      const points = 18;
      ctx.save();
      ctx.translate(x, y);
      ctx.beginPath();
      for (let index = 0; index < points * 2; index += 1) {
        const angle = (Math.PI * index) / points;
        const length = index % 2 === 0 ? radius : radius * 0.34;
        const px = Math.cos(angle) * length;
        const py = Math.sin(angle) * length;
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    installScratchHandlers(ctx) {
      if (this.canvasHandlersInstalled) return;
      this.canvasHandlersInstalled = true;

      this.canvas.addEventListener('pointerdown', (event) => {
        if (this.completed) return;
        this.pointerDown = true;
        this.canvas.classList.add('is-scratching');
        this.canvas.setPointerCapture?.(event.pointerId);
        this.lastPoint = this.getPoint(event);
        this.scratchPoint(ctx, this.lastPoint);
        this.queueProgressCheck(ctx);
      });

      this.canvas.addEventListener('pointermove', (event) => {
        if (!this.pointerDown || this.completed) return;
        const point = this.getPoint(event);
        this.scratchLine(ctx, this.lastPoint, point);
        this.lastPoint = point;
        this.queueProgressCheck(ctx);
      });

      ['pointerup', 'pointercancel', 'pointerleave'].forEach((eventName) => {
        this.canvas.addEventListener(eventName, (event) => {
          if (!this.pointerDown) return;
          this.pointerDown = false;
          this.canvas.classList.remove('is-scratching');
          this.canvas.releasePointerCapture?.(event.pointerId);
          this.queueProgressCheck(ctx);
        });
      });
    }

    getPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    }

    scratchPoint(ctx, point) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(point.x, point.y, this.getBrushSize(), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    scratchLine(ctx, from, to) {
      if (!from || !to) return;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = this.getBrushSize() * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      ctx.restore();
    }

    getBrushSize() {
      return window.matchMedia('(max-width: 600px)').matches ? 24 : 32;
    }

    queueProgressCheck(ctx) {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.checkProgress(ctx);
      });
    }

    checkProgress(ctx) {
      if (this.completed || !this.canvas.width || !this.canvas.height) return;
      const imageData = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
      let transparent = 0;
      let total = 0;
      const sampleStep = 28;

      for (let index = 3; index < imageData.length; index += 4 * sampleStep) {
        total += 1;
        if (imageData[index] < 80) transparent += 1;
      }

      const ratio = total ? transparent / total : 0;
      if (this.status) {
        const percent = Math.min(99, Math.round(ratio * 100));
        this.status.textContent = ratio >= this.threshold
          ? this.dataset.revealedText
          : `${this.dataset.scratchingText || 'Continue raspando'} (${percent}%)`;
      }

      if (ratio >= this.threshold) this.complete();
    }

    complete(fromFallback = false) {
      if (this.completed) return;
      this.completed = true;
      window.clearTimeout(this.emailTimer);
      this.classList.add('is-revealed');
      this.canvas?.classList.add('is-complete');
      this.launchConfetti();
      if (this.status) {
        this.status.textContent = this.dataset.revealedText || 'Pronto, seu presente de aniversário foi liberado.';
      }
      this.revealButtons.forEach((button) => {
        button.hidden = true;
      });
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.emailTimer = window.setTimeout(
        () => this.showEmailPanel({ focus: fromFallback }),
        reduceMotion ? 80 : EMAIL_REVEAL_DELAY
      );
    }

    showEmailPanel({ focus = true } = {}) {
      if (!this.emailPanel) return;
      this.classList.add('is-email-open');
      this.emailPanel.hidden = false;
      if (this.status) this.status.textContent = 'Informe seu e-mail para liberar o presente.';
      window.requestAnimationFrame(() => {
        this.classList.add('is-email-visible');
        if (focus) this.emailInput?.focus();
      });
    }

    clearEmailFeedback() {
      if (!this.emailFeedback) return;
      this.emailFeedback.hidden = true;
      this.emailFeedback.textContent = '';
      this.emailInput?.removeAttribute('aria-invalid');
      this.emailInput?.removeAttribute('aria-describedby');
    }

    showEmailError() {
      this.keepEmailPanelOpen();
      if (this.status) this.status.textContent = 'Preencha um e-mail válido.';
      if (!this.emailFeedback) return;

      this.emailFeedback.textContent = 'Preencha um e-mail válido.';
      this.emailFeedback.hidden = false;
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.setAttribute('aria-describedby', this.emailFeedback.id);
      this.emailInput?.focus();
    }

    keepEmailPanelOpen() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.emailTimer);
      this.clearMemory();
      this.hidden = false;
      this.completed = true;
      this.classList.remove('is-closing', 'is-redemption-visible');
      this.classList.add('is-visible', 'is-revealed', 'is-email-open', 'is-email-visible');
      this.canvas?.classList.add('is-complete');
      this.revealButtons.forEach((button) => {
        button.hidden = true;
      });
      if (this.emailPanel) this.emailPanel.hidden = false;
      if (this.redemptionPanel) this.redemptionPanel.hidden = true;
      document.body.classList.add('lt-scratch-popup-open');
      document.body.style.overflow = 'hidden';
    }

    showRedemptionPanel() {
      if (!this.redemptionPanel) return;

      this.classList.remove('is-email-visible');
      if (this.emailPanel) this.emailPanel.hidden = true;
      this.classList.add('is-email-open');
      this.redemptionPanel.hidden = false;

      if (this.status) {
        this.status.textContent = 'Cupom liberado. Use para ir direto ao produto.';
      }

      window.requestAnimationFrame(() => {
        this.classList.add('is-redemption-visible');
        this.useCouponButton?.focus();
      });
    }

    async handleUseCouponClick(event) {
      event.preventDefault();
      this.remember(this.winDays, 'won');
      this.setUseCouponLoading(true);

      try {
        await this.applyDiscountToCart();
      } catch (error) {
        console.warn('Nao foi possivel aplicar o cupom da raspadinha via Ajax.', error);
      } finally {
        window.location.assign(this.redirectUrl);
      }
    }

    async applyDiscountToCart() {
      if (!window.fetch || !this.couponCode) return;

      const root = window.Shopify?.routes?.root || '/';
      const cartUpdateUrl = `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
      const response = await window.fetch(cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ discount: this.couponCode })
      });

      if (!response.ok) throw new Error(`Cart discount failed with ${response.status}`);
    }

    setUseCouponLoading(isLoading) {
      if (!this.useCouponButton) return;
      this.useCouponButton.setAttribute('aria-busy', String(isLoading));
      this.useCouponButton.classList.toggle('is-loading', isLoading);
      this.useCouponButton.textContent = isLoading ? 'Aplicando...' : this.useCouponLabel;
      if (this.status && isLoading) this.status.textContent = 'Aplicando cupom e abrindo o produto...';
    }

    launchConfetti() {
      if (!this.dialog) return;
      this.removeConfetti();

      const colors = ['#00b0be', '#006375', '#8fcac3', '#d65a93', '#ffdbe8', '#ffffff'];
      const count = window.matchMedia('(max-width: 600px)').matches ? 36 : 48;
      const width = this.dialog.clientWidth || 640;
      const height = this.dialog.clientHeight || 720;
      const spreadX = Math.min(width * 0.46, 390);
      const fallY = Math.min(height * 0.34, 280);

      const container = document.createElement('div');
      container.className = 'lt-scratch-popup__confetti';
      container.setAttribute('aria-hidden', 'true');

      for (let index = 0; index < count; index += 1) {
        const piece = document.createElement('span');
        const x = Math.round((Math.random() * 2 - 1) * spreadX);
        const y = Math.round((Math.random() * 0.65 + 0.35) * fallY);
        const hop = Math.round(Math.random() * 145 + 95);
        const rotation = Math.round((Math.random() * 2 - 1) * 680);
        const size = Math.round(Math.random() * 5 + 7);
        const delay = Math.round(Math.random() * 170);
        const color = colors[index % colors.length];

        piece.className = `lt-scratch-popup__confetti-piece${Math.random() > 0.64 ? ' is-round' : ''}`;
        piece.style.setProperty('--x', `${x}px`);
        piece.style.setProperty('--y', `${y}px`);
        piece.style.setProperty('--mid-x', `${Math.round(x * 0.48)}px`);
        piece.style.setProperty('--mid-y', `-${hop}px`);
        piece.style.setProperty('--r', `${rotation}deg`);
        piece.style.setProperty('--mid-r', `${Math.round(rotation * 0.42)}deg`);
        piece.style.setProperty('--w', `${size}px`);
        piece.style.setProperty('--h', `${Math.max(4, Math.round(size * 0.56))}px`);
        piece.style.setProperty('--d', `${delay}ms`);
        piece.style.setProperty('--c', color);
        container.appendChild(piece);
      }

      this.confetti = container;
      this.dialog.appendChild(container);
      this.confettiTimer = window.setTimeout(() => this.removeConfetti(), CONFETTI_LIFETIME);
    }

    removeConfetti() {
      window.clearTimeout(this.confettiTimer);
      this.confetti?.remove();
      this.confetti = null;
      this.confettiTimer = null;
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), canvas, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('lt-scratch-discount-popup')) {
    customElements.define('lt-scratch-discount-popup', LtScratchDiscountPopup);
  }
})();
